import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { heart, heartOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useFavorites from 'common/reactHooks/useFavorites';
import { IUserFavorite } from 'appRedux/models/playerModels';
import { selectUserId, selectFavoritesList, selectCasinoId } from 'appRedux/selectors';

interface ILikeButtonProps {
  slotNumber: string;
  fontSize?: 'small' | 'medium' | 'large';
  className?: string;
}

const fontSizeMap = {
  small: '14px',
  medium: '18px',
  large: '22px',
};

/** Membership by mnum/slot id — string or number forms (insight stores mnum). */
export const isIncludedInFavorites = (
  slotNumber: string | number,
  userFavorites: IUserFavorite[] = []
) => {
  const needle = String(slotNumber ?? '').trim();
  if (!needle) return false;
  const needleNum = Number(needle);
  return userFavorites.some((favorite) => {
    const stored = String(favorite.slotNumber ?? '').trim();
    if (!stored) return false;
    return stored === needle || (Number.isFinite(needleNum) && Number(stored) === needleNum);
  });
};

export const LikeButton: React.FC<ILikeButtonProps> = ({
  slotNumber,
  fontSize = 'medium',
  className = '',
}) => {
  const { isFavoritesLoading, handleDeleteFavorite, handleInsertFavorite } = useFavorites({});
  const userId = useSelector(selectUserId);
  const favoritesList = useSelector(selectFavoritesList);
  const casinoId = useSelector(selectCasinoId);

  const isFavorite = isIncludedInFavorites(slotNumber, favoritesList);

  const handleClick = (event: React.MouseEvent<HTMLIonButtonElement>) => {
    event.stopPropagation();

    if (userId && casinoId) {
      if (isFavorite) {
        handleDeleteFavorite(slotNumber, userId, String(casinoId));
      } else {
        handleInsertFavorite(slotNumber, userId, String(casinoId));
      }
    }
  };

  const iconFontSize = fontSizeMap[fontSize];

  return (
    <IonButton
      id="likeButton"
      className={`${isFavorite ? 'is-favorite-true' : 'is-favorite-false'} ${className}`}
      fill="clear"
      size="small"
      onClick={handleClick}
      style={{
        minWidth: 'unset',
        padding: '0',
        paddingTop: '2px',
      }}
    >
      {isFavoritesLoading ? (
        <IonSpinner name="crescent" />
      ) : (
        <IonIcon
          slot="icon-only"
          icon={isFavorite ? heart : heartOutline}
          color={isFavorite ? 'favorite' : 'medium'}
          style={{ fontSize: iconFontSize }}
        />
      )}
    </IonButton>
  );
};
